import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  // CUSTOM KIYO START: Rabattfeld-Verhalten — Outside-Click schliesst das offene
  // <details>, Toggle-Collapse setzt Fehlerzeilen zurueck. Komplett eigener Block
  // (connectedCallback/disconnectedCallback erweitern die Core-Hooks via super).
  /** @type {HTMLDetailsElement | null} */
  #detailsEl = null;

  connectedCallback() {
    super.connectedCallback();
    // KIYO: collapse the open discount input on an outside click (e.g. tapping the
    // product list above or the totals below). The "+" toggle is hidden once the
    // field is open, so without this there'd be no way to close it again.
    document.addEventListener('click', this.#handleOutsideClick);
    // KIYO: clear any error when the field collapses, so reopening starts clean.
    const details = this.closest('details');
    if (details instanceof HTMLDetailsElement) {
      this.#detailsEl = details;
      details.addEventListener('toggle', this.#handleDetailsToggle);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleOutsideClick);
    this.#detailsEl?.removeEventListener('toggle', this.#handleDetailsToggle);
    this.#detailsEl = null;
  }

  /**
   * Clears discount errors when the field collapses → a fresh reopen shows no
   * stale error.
   * @param {Event} event
   */
  #handleDetailsToggle = (event) => {
    const details = event.currentTarget;
    if (details instanceof HTMLDetailsElement && !details.open) this.#resetErrors();
  };

  /** Hides all discount error messages. */
  #resetErrors() {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountError?.classList.add('hidden');
    cartDiscountErrorDiscountCode?.classList.add('hidden');
    cartDiscountErrorShipping?.classList.add('hidden');
    this.refs.cartDiscountErrorGeneric?.classList.add('hidden');
    this.refs.cartDiscountErrorBoxCode?.classList.add('hidden');
  }

  /**
   * Collapses the open discount <details> on an outside click — unless an applied
   * discount pill is showing (an active code must stay visible).
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    const details = this.closest('details');
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (this.querySelector('.cart-discount__pill')) return; // active code → keep open
    const wrapper = this.closest('.cart-discount') ?? details;
    if (event.target instanceof Node && wrapper.contains(event.target)) return; // click inside → ignore
    details.open = false;
  };
  // CUSTOM KIYO END

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    // CUSTOM KIYO START: In-flight-Sperre + Busy-Zustand beim Anwenden
    // (Core war: const abortController = this.#createAbortController(); direkt hier)
    // KIYO: applying takes ~0.9 s end to end (cart read + update, measured live)
    // and core shows NOTHING meanwhile — that reads as "Enter did nothing", the
    // user clicks "Anwenden" on top, and core then ABORTS the running request.
    // Aborting only discards the response client-side: the server still applies
    // the code, the follow-up submit hits the "already on the cart" branch below
    // and the UI never updates. So: a submit while a request is in flight is
    // ignored, and the form shows a busy state (mirrors the remove pill).
    if (this.#activeFetch) return;
    const submitButton = form.querySelector('button[type="submit"]');
    form.setAttribute('aria-busy', 'true');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;
    /** Releases the busy state; on success the morph re-renders the form anyway. */
    const clearBusyState = () => {
      form.removeAttribute('aria-busy');
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
    };
    // CUSTOM KIYO END

    const abortController = this.#createAbortController();

    // CUSTOM KIYO START (Core war: const existingDiscounts = this.#existingDiscounts();
    //                    if (existingDiscounts.includes(discountCodeValue)) return;)
    // KIYO: await — #existingDiscounts reads the cart now, see there.
    // Case-insensitive like removeDiscount: Shopify treats "kiyo10" and
    // "Kiyo10" as the same code, so re-typing an active code in different
    // casing must not produce a duplicate submit.
    // #existingDiscounts cannot throw (internal try/catch with pill-scan
    // fallback), so awaiting it outside the try block below is safe.
    const existingDiscounts = await this.#existingDiscounts();
    if (existingDiscounts.some((code) => code.toLowerCase() === discountCodeValue.toLowerCase())) {
      // KIYO: core returned silently here. The input is only visible while no
      // code is applied (the pill hides it), so a code that is "already on the
      // cart" at this point is one Shopify accepted but cannot apply yet
      // (`applicable: false`, e.g. below the minimum order value). Show the same
      // message the server path shows for exactly that case instead of a dead
      // end where Enter/click visibly do nothing. #activeFetch must be released
      // by hand: nothing was fetched, so the `finally` below never runs.
      this.#activeFetch = null;
      clearBusyState();
      discountCode.value = '';
      this.#resetErrors();
      this.#handleDiscountError(
        this.#isGiftItemMissing(discountCodeValue, this.#lastCartRead ?? {}) ? 'box_code' : 'discount_code'
      );
      return;
    }
    // CUSTOM KIYO END

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    // CUSTOM KIYO START: zwei zusaetzliche Fehlerzeilen zuruecksetzen
    // KIYO: also reset the generic error line so it can't linger next to another message.
    this.refs.cartDiscountErrorGeneric?.classList.add('hidden');
    this.refs.cartDiscountErrorBoxCode?.classList.add('hidden');
    // CUSTOM KIYO END

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        // CUSTOM KIYO START (Core war: this.#handleDiscountError('discount_code');)
        // KIYO: BOX-* gift codes (welcome flow) mostly fail because the free
        // Box S itself is missing — Shopify never auto-adds the "get Y" item
        // of a Buy-X-get-Y discount. In that case show a message that names
        // the fix instead of the generic error. Prefix + product id come from
        // data attributes on this component in cart-summary.liquid.
        this.#handleDiscountError(this.#isGiftItemMissing(discountCodeValue, data) ? 'box_code' : 'discount_code');
        // KIYO: deliberately NOT detaching the rejected code — Shopify keeps it
        // on the cart and auto-applies it once its conditions are met (e.g. the
        // minimum cart value is reached). That's wanted: the customer shouldn't
        // lose the discount. The pill then becomes visible via declarative-open
        // in cart-summary.liquid. Details: THEME-CHANGES.md 2026-07-23.
        // CUSTOM KIYO END
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        // CUSTOM KIYO START: sichtbare Fehlerzeile zusaetzlich zum CartErrorEvent
        // KIYO: don't swallow failures silently. An aborted request (rapid
        // re-submit via #createAbortController) is filtered above; anything else
        // (network/offline, non-JSON 4xx/5xx) gets a visible generic error line
        // instead of a silent no-op right before checkout. console.error is
        // deliberate telemetry, not debug output.
        console.error(error);
        this.#handleDiscountError('generic');
        // CUSTOM KIYO END
      }
    } finally {
      this.#activeFetch = null;
      // CUSTOM KIYO START: Busy-Zustand freigeben
      clearBusyState();
      // CUSTOM KIYO END
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      // CUSTOM KIYO START (Core war: !(event.target instanceof HTMLElement) ||)
      // KIYO: `Element` instead of `HTMLElement` — a click hitting the × button's
      // svg/path icon (an SVGElement, half the button's hit area) was silently
      // swallowed, so removing a discount often needed several clicks.
      // `closest()` below works on SVG elements too. THEME-CHANGES.md 2026-07-23.
      !(event.target instanceof Element) ||
      // CUSTOM KIYO END
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    // CUSTOM KIYO START: Busy-State + Doppelklick-Sperre. Ersetzt zugleich die
    // Core-Zeilen `const existingDiscounts = this.#existingDiscounts(); const index =
    // existingDiscounts.indexOf(discountCode); if (index === -1) return;
    // existingDiscounts.splice(index, 1);` — siehe ZWISCHENLOESUNG unten.
    // KIYO: busy state BEFORE anything async. Removing takes ~850ms end to end
    // (cart read ~320ms + update ~525ms, live gemessen) and the stock code shows
    // NOTHING in between — the row just sits there. Users read that as "the
    // button is broken", click again, and the second click aborts the first via
    // #createAbortController: now genuinely nothing happens. Reported exactly
    // that way ("einmal ging es, dann nicht mehr").
    // The guard also makes a double click harmless.
    const removeButton = event.target.closest('.cart-discount__pill-remove');
    if (removeButton instanceof HTMLButtonElement) {
      if (removeButton.disabled) return; // a removal is already in flight
      removeButton.disabled = true;
    }
    pill.setAttribute('aria-busy', 'true');

    /** Restores the pill if the request fails — on success the morph replaces it anyway. */
    const clearBusyState = () => {
      pill.removeAttribute('aria-busy');
      if (removeButton instanceof HTMLButtonElement) removeButton.disabled = false;
    };
    // CUSTOM KIYO END

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        // CUSTOM KIYO START (Core war: discountCodes: existingDiscounts.map((code) => ({ code })),)
        // KIYO: the ZWISCHENLOESUNG below clears ALL codes (discount: ''), and the
        // core `existingDiscounts` list no longer exists here — the resulting
        // code list after this update is simply empty.
        discountCodes: [],
        // CUSTOM KIYO END
        promise: deferredPromise.promise,
      })
    );

    try {
      // CUSTOM KIYO START (Core war: body: JSON.stringify({ discount:
      // existingDiscounts.join(','), sections: [this.dataset.sectionId] }))
      // KIYO ZWISCHENLOESUNG (2026-07-25): "×" leert ALLE Rabattcodes, statt nur
      // den angeklickten zu entfernen.
      //
      // Warum: Der Warenkorb kann Codes tragen, die Shopify akzeptiert, aber noch
      // nicht anwendet (`applicable: false`) — etwa ein BOX-Gratiscode unterhalb
      // seiner 349-EUR-Schwelle. Die Pillen zeigen nur den AKTIVEN Code. Entfernt
      // man den, rueckt ein wartender nach und wird sofort aktiv: gemessen
      // "BOX-4W928DTT AKTIV + Kiyo10 wartet" -> nach dem Klick "Kiyo10 AKTIV".
      // Fuer den Kunden steht danach unveraendert ein Rabatt da — das liest sich
      // als "laesst sich nicht entfernen" und wurde genau so gemeldet.
      //
      // Solange wartende Codes unsichtbar sind, ist "× raeumt alles weg" das
      // ehrlichere Verhalten: eine sichtbare Pille, ein Klick, kein Rabatt mehr.
      // GEPLANT (Variante A): wartende Codes mit eigenem Zustand anzeigen
      // ("wird ab 349 € angewendet"), dann wieder gezielt einzeln entfernen.
      //
      // Nebeneffekt: kein Cart-Read noetig (spart ~320ms pro Entfernen) und der
      // Gross-/Kleinschreibungs-Vergleich entfaellt — die Pille traegt den
      // kanonischen Titel ("Kiyo10"), der Warenkorb den getippten ("kiyo10").
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: '', sections: [this.dataset.sectionId] }),
      });
      // CUSTOM KIYO END

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        // CUSTOM KIYO START: Fehler zusaetzlich in der Konsole sichtbar machen
        // KIYO: surface the failure in the console. On failure no morph ran, so the
        // pill is left untouched — the unchanged UI already signals "nothing
        // happened" to the user (no false-removed state to recover from).
        // console.error is deliberate telemetry, not debug output.
        console.error(error);
        // CUSTOM KIYO END
      }
      // CUSTOM KIYO START: Busy-State freigeben
      // KIYO: release the busy state so the × stays usable after a failed or
      // aborted attempt. On success the morph replaces the pill anyway.
      clearBusyState();
      // CUSTOM KIYO END
    } finally {
      this.#activeFetch = null;
    }
  };

  // CUSTOM KIYO START: komplette Methode #isGiftItemMissing (neu, kein Core-Pendant)
  /**
   * KIYO EINSCHUB: true when the rejected code is a gift code (prefix list,
   * e.g. "BOX-,KIYO-BOX-") and the gift product (Box S) is missing from the
   * cart — the one failure the customer can fix directly by adding the box.
   * Every other reason (e.g. below the 349 EUR minimum) keeps the stock
   * message, which already mentions the minimum order value.
   *
   * "In the cart" includes the box sitting INSIDE a configuration/bundle
   * line: cart.js only exposes the parent line there, so the Liquid side
   * checks item_components and hands the result in via
   * data-gift-product-present. The cart.items scan stays as a fallback for
   * the separate-line case if that attribute is ever missing.
   *
   * @param {string} code - The discount code the customer entered.
   * @param {{ items?: { product_id: number }[] }} cart - Cart payload of the update response.
   * @returns {boolean}
   */
  #isGiftItemMissing(code, cart) {
    const prefixes = (this.dataset.giftCodePrefix ?? '')
      .split(',')
      .map((p) => p.trim().toUpperCase())
      .filter(Boolean);
    const productId = Number(this.dataset.giftProductId);
    if (prefixes.length === 0 || !productId) return false;
    const codeUpper = code.toUpperCase();
    if (!prefixes.some((prefix) => codeUpper.startsWith(prefix))) return false;
    if (this.dataset.giftProductPresent === 'true') return false;
    const items = Array.isArray(cart.items) ? cart.items : [];
    return !items.some((item) => item.product_id === productId);
  }
  // CUSTOM KIYO END

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'generic' | 'box_code'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    // CUSTOM KIYO START (Core war: const target = type === 'discount_code' ?
    // cartDiscountErrorDiscountCode : cartDiscountErrorShipping; und unten
    // target.classList statt target?.classList)
    let target;
    if (type === 'discount_code') {
      target = cartDiscountErrorDiscountCode;
    } else if (type === 'shipping') {
      target = cartDiscountErrorShipping;
    } else if (type === 'box_code') {
      // KIYO: defensive like the generic ref — falls back to the stock error
      // line if a theme update ever drops the element.
      target = this.refs.cartDiscountErrorBoxCode ?? cartDiscountErrorDiscountCode;
    } else {
      // KIYO: generic network/server error line. Accessed defensively (not in
      // requiredRefs) so a theme update that drops the ref can't break the component.
      target = this.refs.cartDiscountErrorGeneric;
    }
    // CUSTOM KIYO END
    cartDiscountError.classList.remove('hidden');
    target?.classList.remove('hidden');

    // CUSTOM KIYO START (Core war: const errorMessage = type === 'discount_code'
    // ? 'Invalid discount code' : 'Discount not applicable for shipping';)
    // KIYO: the extra Kiyo error types need their own event message — the core
    // ternary would report every non-discount_code type as a shipping error.
    let errorMessage;
    if (type === 'discount_code') {
      errorMessage = 'Invalid discount code';
    } else if (type === 'shipping') {
      errorMessage = 'Discount not applicable for shipping';
    } else if (type === 'box_code') {
      errorMessage = 'Gift product for this discount code is missing from the cart';
    } else {
      errorMessage = 'Discount could not be applied';
    }
    // CUSTOM KIYO END
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   *
   * KIYO EINSCHUB: reads the authoritative list from the cart instead of the
   * visible pills. Every discount request sends this list as a FULL REPLACEMENT
   * — anything missing from it is dropped from the cart.
   *
   * A code that Shopify accepted but cannot apply yet (e.g. the BOX-* free-gift
   * codes below their 349 EUR threshold) has no pill: it sits on the cart with
   * `applicable: false`, waiting for its condition to be met. The pill-based
   * version therefore silently deleted exactly the codes that applyDiscount
   * deliberately keeps (see the KIYO note there) — the customer entered the
   * code, added more to the cart, and the free box never appeared.
   * Reproduced live 2026-07-25. Details: THEME-CHANGES.md.
   *
   * Falls back to the stock pill scan if the cart cannot be read, so a failed
   * request degrades to the previous behaviour instead of wiping all codes.
   *
   * @returns {Promise<string[]>}
   */
  // CUSTOM KIYO START: Methode ist jetzt async (Core war: #existingDiscounts() ohne
  // async); der try/fetch-Block ist neu, der Pill-Scan darunter ist Core-Fallback.
  /** Last cart payload read by #existingDiscounts — lets applyDiscount pick the
   *  right message for a code that is already on the cart (see there). */
  /** @type {{ items?: { product_id: number }[] } | null} */
  #lastCartRead = null;

  async #existingDiscounts() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (response.ok) {
        const cart = await response.json();
        this.#lastCartRead = cart;
        if (Array.isArray(cart.discount_codes)) {
          return cart.discount_codes
            .map((/** @type {{ code: string }} */ entry) => entry.code)
            .filter((/** @type {string} */ code) => typeof code === 'string' && code.length > 0);
        }
      }
    } catch (error) {
      // fall through to the pill scan below
    }
    // CUSTOM KIYO END — ab hier Core (Pill-Scan als Fallback)

    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
